import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons/faCalendarDays';
import { faArrowTrendUp } from '@fortawesome/pro-light-svg-icons/faArrowTrendUp';
import { faListMusic } from '@fortawesome/pro-light-svg-icons/faListMusic';
import { faBell } from '@fortawesome/pro-light-svg-icons/faBell';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-light-svg-icons/faArrowUpRightFromSquare';
import { TypedMemo } from '../../../common/types';
import { SMLUpgradeFeature } from '../../../music24/config/music24PlanCapabilities';
import { helpArticleUrl } from '../../../support/helpCenter/helpCenterUrl';
import { notificationsMessages as m } from './notificationsMessages';
import type { NotificationPreferences, NotificationPreferenceKey } from './useNotificationPreferences';
import { DigestTiles } from './DigestTiles';
import { SwitchRow } from './SwitchRow';
import { ChoiceChips } from './ChoiceChips';
import { Panel, PanelFoot, PanelHead } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
import styles from './notifications.module.scss';
/* eslint-enable css-modules/no-unused-class */

const messages = defineMessages({
  group: { id: 'app.sml.nav.group.public', defaultMessage: 'Public intelligence' },
  digest: { id: 'app.sml.settings.notifications.digest.title', defaultMessage: 'Digest' },
  alerts: { id: 'app.sml.settings.notifications.alerts.title', defaultMessage: 'Movement alerts' },
  alertsDescription: {
    id: 'app.sml.settings.notifications.alerts.toggle.description',
    defaultMessage: 'One email the day anything you follow jumps more than your threshold.',
  },
  playlist: { id: 'app.sml.settings.notifications.playlistAlerts.title', defaultMessage: 'Playlist alerts' },
  oneK: { id: 'app.sml.settings.notifications.playlistAlerts.threshold.1k', defaultMessage: '1K followers' },
  tenK: { id: 'app.sml.settings.notifications.playlistAlerts.threshold.10k', defaultMessage: '10K followers' },
  hundredK: { id: 'app.sml.settings.notifications.playlistAlerts.threshold.100k', defaultMessage: '100K followers' },
  seePlans: { id: 'app.sml.nav.seePlans', defaultMessage: 'See plans' },
});

interface Props {
  prefs: NotificationPreferences;
  /** The workspace plan sends daily digests and alerts (`realtimeAlerts`). */
  hasRealtimeAlerts: boolean;
  update: <K extends NotificationPreferenceKey>(key: K, value: NotificationPreferences[K]) => void;
  openUpgrade: () => void;
}

/** Public intelligence: the digest and the two alerts, all about the watchlist (design settings-redesign c1, c2). */
const PublicIntelligenceGroupComponent: React.FC<Props> = ({ prefs, hasRealtimeAlerts, update, openUpgrade }) => {
  const intl = useIntl();
  const locked = hasRealtimeAlerts ? undefined : SMLUpgradeFeature.realtimeAlerts;

  return (
    <Panel as="section">
      <PanelHead title={intl.formatMessage(messages.group)} subtitle={intl.formatMessage(m.publicDescription)} />
      {!hasRealtimeAlerts && (
        <div className={styles.upsell}>
          <FontAwesomeIcon icon={faBell} />
          <p className={styles.upsellText}>{intl.formatMessage(m.freeNote)}</p>
          <Button color="secondary" size="sm" onClick={openUpgrade}>
            {intl.formatMessage(messages.seePlans)}
          </Button>
        </div>
      )}
      <div className={`${cards.row} ${cards.rowFlat}`}>
        <span className={`${cards.rowIcon} ${cards.rowIconBrand}`}>
          <FontAwesomeIcon icon={faCalendarDays} />
        </span>
        <div className={cards.rowText}>
          <span className={cards.rowTitle}>{intl.formatMessage(messages.digest)}</span>
          <span className={cards.rowSub}>
            {!hasRealtimeAlerts && prefs.reportFrequency === 'daily'
              ? intl.formatMessage(m.dailyOnFree)
              : intl.formatMessage(m.digestDescription)}
          </span>
        </div>
      </div>
      <div className={styles.options}>
        <DigestTiles
          value={prefs.reportFrequency}
          dailyLocked={!hasRealtimeAlerts}
          onChange={(value) => update('reportFrequency', value)}
          onLockedClick={openUpgrade}
        />
      </div>
      <SwitchRow
        icon={faArrowTrendUp}
        title={intl.formatMessage(messages.alerts)}
        description={intl.formatMessage(messages.alertsDescription)}
        checked={prefs.alertsEnabled}
        onChange={(value) => update('alertsEnabled', value)}
        lockedFeature={locked}
        onLockedClick={openUpgrade}
        flat={hasRealtimeAlerts}
        divided
      />
      {hasRealtimeAlerts && (
        <div className={styles.optionRow}>
          <span className={styles.optionLabel}>{intl.formatMessage(m.threshold)}</span>
          <ChoiceChips
            name="alertThreshold"
            label={intl.formatMessage(m.threshold)}
            options={[10, 20, 40].map((value) => ({ value: value as 10 | 20 | 40, label: `${value}%` }))}
            value={prefs.alertThresholdPercent}
            disabled={!prefs.alertsEnabled}
            onChange={(value) => update('alertThresholdPercent', value)}
          />
        </div>
      )}
      <SwitchRow
        icon={faListMusic}
        title={intl.formatMessage(messages.playlist)}
        description={intl.formatMessage(m.playlistDescription)}
        checked={prefs.playlistAlertsEnabled}
        onChange={(value) => update('playlistAlertsEnabled', value)}
        lockedFeature={locked}
        onLockedClick={openUpgrade}
        flat={hasRealtimeAlerts}
        divided
      />
      {hasRealtimeAlerts && (
        <div className={styles.optionRow}>
          <span className={styles.optionLabel}>{intl.formatMessage(m.playlistFloor)}</span>
          <ChoiceChips
            name="playlistAlertMinFollowers"
            label={intl.formatMessage(m.playlistFloor)}
            options={[
              { value: 1000 as const, label: intl.formatMessage(messages.oneK) },
              { value: 10000 as const, label: intl.formatMessage(messages.tenK) },
              { value: 100000 as const, label: intl.formatMessage(messages.hundredK) },
            ]}
            value={prefs.playlistAlertMinFollowers}
            disabled={!prefs.playlistAlertsEnabled}
            onChange={(value) => update('playlistAlertMinFollowers', value)}
          />
        </div>
      )}
      <PanelFoot>
        <span>{intl.formatMessage(m.publicFooter)}</span>
        <a
          className={`${cards.link} ms-auto`}
          href={helpArticleUrl('reports-and-alerts', 'digests')}
          target="_blank"
          rel="noopener noreferrer"
        >
          {intl.formatMessage(m.howItWorks)}
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </a>
      </PanelFoot>
    </Panel>
  );
};

export const PublicIntelligenceGroup = TypedMemo(PublicIntelligenceGroupComponent);
